import React, { Component, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import GeckoLocation from 'services/gecko-location';
import { GeckoModalService, GeckoModal } from 'ui';
import isDeepEqual from 'utilities/object-comparison';

const defaultDialog = (customText) => {
  const buttons = [
    {
      name: 'Cancel\u2060',
      type: 'default',
      icon: 'far fa-times',
      handler: () => new GeckoModalService().close(false),
    },
    {
      name: 'Discard Changes',
      type: 'danger',
      icon: 'far fa-trash-alt',
      handler: () => new GeckoModalService().close(true),
    },
  ];
  return new GeckoModalService().open(
    () => {
      return (
        <GeckoModal btns={buttons} name="Unsaved Changes">
          {customText ||
            'You have unsaved changes. Do you wish to discard these?'}
        </GeckoModal>
      );
    },
    { size: 'md' }
  );
};

class UnsavedChanges extends Component {
  static propTypes = {
    dirty: PropTypes.bool,
    dirtyWhen: PropTypes.func,
    warningText: PropTypes.string,
    children: PropTypes.any,
    customDialog: PropTypes.func,
  };

  static defaultProps = {
    dirty: false,
    dirtyWhen: () => {},
    warningText: 'You have unsaved changes. Do you wish to discard these?',
  };

  UNSAFE_componentWillMount() {
    this.changeHandler = GeckoLocation.beforeChange(() => {
      if (this.props.dirtyWhen() || this.props.dirty) {
        if (this.props.customDialog) {
          return this.props.customDialog().then((result) => !!result);
        }
        return defaultDialog(this.props.warningText).then((result) => !!result);
      } else {
        return true;
      }
    });
  }

  componentWillUnmount() {
    // unregister the listener
    this.changeHandler();
  }

  render() {
    return <div className="UnsavedChanges">{this.props.children}</div>;
  }
}

// This Higher Order Component takes an array of tracked state values e.g. ['value'] and will fire unsaved changes alerts if you try
//to navigate away and that state has changed.  It will also expose a clearUnsavedChanges function to the child which can be used for
//clearing the alerts.
export const UnsavedChangeble =
  (trackedValues, customText) => (WrappedComponent) => {
    // Check if 2 values are different, we can potentially make this more efficient if it becomes a problem
    const simpleIsDirty = (val1, val2) => !isDeepEqual(val1, val2);

    // Get only the parts of the state we're interested in
    const getInterestingState = (state) =>
      trackedValues.reduce((values, valueKey) => {
        return { ...values, [valueKey]: state[valueKey] };
      }, {});

    return class extends Component {
      _state = {};

      componentDidMount() {
        const initialValues = getInterestingState(this.comp.state);
        this._state = initialValues; // Can't use real state as we don't want to trigger the childs update hooks when we change
      }

      render() {
        const clearUnsavedChanges = () => {
          if (!this.comp) {
            return;
          }
          // Clear the unsaved alert by making the internal state equal to the component state
          const initialValues = getInterestingState(this.comp.state);
          this._state = initialValues;
        };
        const dirtyWhen = () => {
          if (!this.comp) {
            return false;
          }
          return simpleIsDirty(
            getInterestingState(this.comp.state),
            this._state
          );
        };
        return (
          <UnsavedChanges dirtyWhen={dirtyWhen} warningText={customText}>
            <WrappedComponent
              {...this.props}
              clearUnsavedChanges={clearUnsavedChanges.bind(this)}
              ref={(elem) => (this.comp = elem)}
            ></WrappedComponent>
          </UnsavedChanges>
        );
      }
    };
  };

export function useUnsavedChanges(
  trackedValues,
  { customText, customDialog } = {}
) {
  const [dirty, setDirty] = useState(false);
  const initialRef = useRef();

  useEffect(() => {
    setDirty(!isDeepEqual(initialRef.current, trackedValues));
  }, trackedValues);

  const dialog = customDialog || defaultDialog.bind(null, customText);
  const locationChangeHandler = () => {
    if (dirty) {
      return dialog().then((result) => Boolean(result));
    }
    return true;
  };

  useEffect(() => GeckoLocation.beforeChange(locationChangeHandler));

  const setInitialValue = (val) => (initialRef.current = val);

  const setToClean = () => setDirty(false);
  const setToDirty = () => setDirty(true);
  return [setToClean, setToDirty, setInitialValue, dirty];
}

export default UnsavedChanges;
